import { applyMiddleware, compose, createStore as createReduxStore } from 'redux';
import { createRouterMiddleware, initialRouterState } from 'connected-next-router';
import { createWrapper } from 'next-redux-wrapper';
import { persistStore } from 'redux-persist';
import Router from 'next/router';
import thunk from 'redux-thunk';

import { AppStore } from './types';
import analyticsMiddleware from './analytics';
import browser from '../utils/browser';
import cmsMiddleware from '../cms/middleware';
import createRootReducer, { RootState } from '../reducers';
import getIsDevelopmentEnv from '../utils/get-is-development-env';
import getIsServer from '../utils/get-is-server';
import importedApi from '../models/api';
import promiseMiddleware from './promise-middleware';

const createStore = (api: unknown, data: Partial<RootState>) => {
  let composeEnhancers;
  const isDevelopmentEnv = getIsDevelopmentEnv();
  const isServer = getIsServer();
  if (isDevelopmentEnv && !isServer) {
    const { __REDUX_DEVTOOLS_EXTENSION_COMPOSE__ } = browser.getWindow();
    composeEnhancers =
      (__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ && __REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true })) || compose;
  } else {
    composeEnhancers = compose;
  }

  const middleware = applyMiddleware(
    promiseMiddleware(api),
    analyticsMiddleware,
    thunk,
    createRouterMiddleware(),
    cmsMiddleware,
  );
  const finalCreateStore = composeEnhancers(middleware)(createReduxStore);
  const store = finalCreateStore(createRootReducer(), data);
  const persistor = persistStore(store);

  // We store persistor on the store itself, as we need it for PersistGate.
  // nextReduxWrapper creates the store and persistor but does not make the persistor available to us.
  // This approach is demonstrated in the docs: https://github.com/kirill-konshin/next-redux-wrapper.
  return { store: Object.assign(store, { __persistor: persistor }) };
};

export default createStore;

const nextReduxWrapper = createWrapper<AppStore>((context) => {
  const ctx = ('ctx' in context ? context.ctx : undefined) ?? Router.router;
  const asPath = ctx?.asPath;
  const initialState = asPath ? { router: initialRouterState(asPath) } : {};
  return createStore(importedApi, initialState).store;
});

export { nextReduxWrapper };
